// Theme engine: CSS variables on <html data-theme>. Default follows the device
// (prefers-color-scheme); a stored 'dark'|'light' overrides it. Switch UI is a later slice.
export type ThemePref = 'system' | 'dark' | 'light';

const KEY = 'tcn.theme';
const THEME_COLOR = { dark: '#090806', light: '#F4EFE6' } as const;
const query = () => window.matchMedia('(prefers-color-scheme: light)');

export function getThemePref(): ThemePref {
  try {
    const v = window.localStorage.getItem(KEY);
    return v === 'dark' || v === 'light' ? v : 'system';
  } catch {
    return 'system';
  }
}

/** Resolve the preference, set <html data-theme> and meta theme-color. Call before first render. */
export function applyTheme(): void {
  const pref = getThemePref();
  const systemTheme = query().matches ? 'light' : 'dark';
  const theme = pref === 'system' ? systemTheme : pref;
  document.documentElement.dataset.theme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

export function setThemePref(pref: ThemePref): void {
  try {
    if (pref === 'system') window.localStorage.removeItem(KEY);
    else window.localStorage.setItem(KEY, pref);
  } catch {
    // storage unavailable: the preference just doesn't persist
  }
  applyTheme();
}

// Re-resolve when the OS setting changes (no-op for an explicit dark/light pref).
export function watchSystemTheme(): void {
  query().addEventListener('change', () => {
    if (getThemePref() === 'system') applyTheme();
  });
}
